package data.objects.views.directediting

import com.tryformation.localization.Translatable
import data.objects.ActiveObjectStore
import dev.fritz2.core.HtmlTag
import dev.fritz2.core.RenderContext
import dev.fritz2.core.id
import dev.fritz2.core.storeOf
import dev.fritz2.headless.foundation.utils.floatingui.utils.Placement
import dev.fritz2.headless.foundation.utils.floatingui.utils.PlacementValues
import koin.withKoin
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import localization.getTranslationFlow
import localization.translate
import mapsearch.historyPathTool.ObjectHistoryResultsStore
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLDivElement
import theme.FormationIcons
import theme.FormationUIIcons
import theme.IconEnum
import twcomponents.doOnceWhenElementInDOM
import twcomponents.twColOfNoGap
import twcomponents.twIconMedium
import twcomponents.twLargeIconButton
import twcomponents.twMediumIconButton
import twcomponents.twRowOfJustifyBetween
import twcomponents.twRowOfJustifyStart
import twcomponents.twSimpleTextTooltipWithSubtitle
import utils.ToggleStore
import utils.resizeObserver

/**
 * ObjectActionButtonMenu
 */

data class ActionBtn(
    val icon: IconEnum,
    val title: Translatable? = null,
    val titleArgs: Map<String, Any>? = null,
    val tooltip: Translatable? = null,
    val tooltipArgs: Map<String, Any>? = null,
    val tooltipSub: Translatable? = null,
    val tooltipSubArgs: Map<String, Any>? = null,
    val tooltipPlacement: Placement = PlacementValues.top,
    val specialClasses: String? = null,
    val block: HtmlTag<HTMLButtonElement>.() -> Unit,
)

/**
 * @return how many buttons fit completely into `containerWidth`
 */
private fun calcVisible(
    widths: List<Int>,
    containerWidth: Int,
    overflowIconWidth: Int
): Int {
    var used = overflowIconWidth  // reserve room for the DotMenu icon
    for ((idx, w) in widths.withIndex()) {
        if (used + w > containerWidth) return idx
        used += w
    }
    return widths.size                     // everything fits
}

/**
 * ObjectActionButtonMenu
 */

fun RenderContext.objectActionButtonMenu() {
    withKoin {
        val activeObjectStore: ActiveObjectStore = get()
        val objectHistoryResultsStore: ObjectHistoryResultsStore = get()

        div("w-full h-10") {
            combine(activeObjectStore.data, objectHistoryResultsStore.data) { o, h ->
                // Create a list of actionButtons
                actionButtons(o, h)
            }.render { buttons ->

                val visibleCount = storeOf(buttons.size) // reacts to every resize of the parent container
                val dropdownOpen = ToggleStore(false)

                twRowOfJustifyBetween("relative") {
                    //  temporarily render placeholders to measure width
                    val measureReady = storeOf(false)
                    val btnWidths = mutableListOf<Int>()

                    buttons.forEachIndexed { i, actionBtn ->
                        twMediumIconButton(actionBtn.icon) {
                            className(
                                (actionBtn.specialClasses ?: "")
                                    + " opacity-0 flex flex-row items-center justify-start gap-2 hover:bg-gray-100 p-2.5 rounded-xl",
                            ) // bg-gray-100
                            val btnNode = domNode
                            actionBtn.title?.let {
                                actionBtn.title.getTranslationFlow(actionBtn.titleArgs).render { title ->
                                    p("text-xs font-bold whitespace-nowrap") title@{
                                        +title
                                        // Wait until the Title is rendered, then measure the size of the button
                                        doOnceWhenElementInDOM(domNode = this@title.domNode) {
                                            if (!measureReady.current) {
//                                                console.log("~Button measure (${actionBtn.title}, $i) -> ${btnNode.offsetWidth}")
                                                btnWidths += btnNode.offsetWidth
                                                // remove from DOM after measure
                                                btnNode.remove()
                                            }
                                            if (i == buttons.lastIndex) {
                                                measureReady.update(true)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // DotMenu icon placeholder (for width)
                    val kebabWidth = 52

                    //  Resize observer
                    resizeObserver(domNode).map { element ->
                        element.width
                    }.distinctUntilChanged().debounce(100)
                        .render { containerWidth ->
                            val calc = calcVisible(btnWidths, containerWidth, kebabWidth)
                            visibleCount.update(calc)
                            // Actual, re-rendered buttons
                            twRowOfJustifyStart {
                                buttons.take(calc).forEachIndexed { index, actionBtn ->
                                    objectActionButtonMediumIconShort(
                                        actionBtn.copy(
                                            // Make sure the Tooltips for first and last are shifted inwards
                                            tooltipPlacement = when (index) {
                                                0 -> PlacementValues.topStart
                                                calc - 1 -> PlacementValues.topEnd
                                                else -> actionBtn.tooltipPlacement
                                            },
                                        ),
                                    )
                                }
                            }
                        }

                    // Overflow trigger
                    visibleCount.data.render { vis ->
//                        console.log("~Visible count -> $vis")
                        if (vis < buttons.size) {
//                            console.log("~Visible count -> Show Menu")
                            div(
                                baseClass = "flex flex-col items-end justify-start z-[1019] rounded-2xl bg-formationWhite transition-[height] duration-300 ease-in-out",
                            ) {
                                className(dropdownOpen.data.map { if (it) "absolute -right-2 -top-2 p-2 shadow-2xl" else "" })

                                // For Mobile
                                focusouts.debounce(300) handledBy {
                                    dropdownOpen.update(false)
                                }
                                // For Desktop
                                mouseleaves.debounce(500) handledBy {
                                    dropdownOpen.update(false)
                                }

                                button("cursor-pointer hover:bg-gray-100 p-2.5 rounded-xl") {
                                    dropdownOpen.data.render { open ->
                                        if (open) {
                                            twIconMedium(FormationUIIcons.ChevronUp)
                                        } else {
                                            twIconMedium(FormationIcons.DotMenu)
                                        }
                                    }

                                    // For Mobile
                                    clicks handledBy dropdownOpen.toggle
                                    // For Desktop
                                    mouseenters handledBy {
                                        dropdownOpen.update(true)
                                    }
                                }

                                objectActionButtonListWrapper {
                                    id("objectActionButtonListWrapper")
                                    className(dropdownOpen.data.map { open -> if (open) "block " else "hidden" }, "hidden")
                                    buttons.drop(vis).forEach { actionBtn ->
                                        twLargeIconButton(actionBtn.icon) {
                                            className(actionBtn.specialClasses + " flex flex-row items-center justify-start gap-2 hover:bg-gray-100 p-2.5 rounded-xl")
                                            twColOfNoGap("items-start justify-center") {
                                                actionBtn.tooltip?.let {
                                                    p("text-xs font-bold") {
                                                        translate(actionBtn.tooltip, actionBtn.tooltipArgs)
                                                    }
                                                }
                                                actionBtn.tooltipSub?.let {
                                                    p("text-xs font-light") {
                                                        translate(actionBtn.tooltipSub, actionBtn.tooltipSubArgs)
                                                    }
                                                }
                                            }
                                            actionBtn.block.invoke(this)
                                            clicks handledBy {
                                                dropdownOpen.update(false)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

fun RenderContext.objectActionButtonMediumIconShort(
    actionBtn: ActionBtn
) = twMediumIconButton(actionBtn.icon) {
    className(actionBtn.specialClasses + " flex flex-row items-center justify-start gap-2 hover:bg-gray-100 p-2.5 rounded-xl") // bg-gray-100
    actionBtn.title?.let {
        p("text-xs font-bold whitespace-nowrap") {
            translate(actionBtn.title, actionBtn.titleArgs)
        }
    }
    actionBtn.block.invoke(this)
}.twSimpleTextTooltipWithSubtitle(
    title = actionBtn.tooltip,
    titleArgs = actionBtn.tooltipArgs,
    subTitle = actionBtn.tooltipSub,
    subTitleArgs = actionBtn.tooltipSubArgs,
    positioning = actionBtn.tooltipPlacement,
)


/**
 * Wrapper for ObjectActionButtons
 * vertical list
 * or side scrolling
 */

fun RenderContext.objectActionButtonListWrapper(content: HtmlTag<HTMLDivElement>.() -> Unit) {
    twColOfNoGap(
        additionalClasses = "w-max max-w-full items-stretch justify-center gap-2 overflow-y-auto",
    ) {
        content.invoke(this)
    }
}

//fun RenderContext.objectActionButtonSideScrollWrapper(content: HtmlTag<HTMLDivElement>.() -> Unit) {
//
//    val overflowLeft = storeOf(false)
//    val overflowRight = storeOf(false)
//
//    twRowOfNoGap(
//        additionalClasses = "pt-2 px-3 w-full items-center justify-start gap-2 overflow-x-auto",
//    ) {
//        inlineStyle(
//            """
//            -ms-overflow-style: none;  /* IE and Edge */
//            scrollbar-width: none;  /* Firefox */
//            """.trimIndent(),
//        )
//
//        // Updating the overflow stores, once the container overflows left or right
//        // LEFT OVERFLOW
//        returnCheckOnElementInDOMContinuously(
//            domNode = this.domNode,
//            check = {
//                (this.domNode as? Element)?.let { el ->
//                    el.scrollLeft > 30.0
//                } ?: false
//            },
//        ) { checkValue ->
////                        console.log("Update LEFT Overflow:", checkValue)
//            overflowLeft.update(checkValue)
//        }
//        // RIGHT OVERFLOW
//        returnCheckOnElementInDOMContinuously(
//            domNode = this.domNode,
//            check = {
//                (this.domNode as? Element)?.let { el ->
//                    (el.scrollWidth - el.scrollLeft) > (el.clientWidth.toDouble() + 30.0)
//                } ?: false
//            },
//        ) { checkValue ->
////                        console.log("Update RIGHT Overflow:", checkValue)
//            overflowRight.update(checkValue)
//        }
//
//        // Left < Chevron button ("move to scroll-start")
//        twMediumIconButton(icon = FormationUIIcons.ChevronLeft) {
//            className("absolute left-1 text-white bg-formationBlack/20 hover:bg-formationBlack/50 rounded-full p-1")
//            className(overflowLeft.data.map { isOverflown -> if (isOverflown) "block" else "hidden" })
//            clicks handledBy {
//                this.domNode.parentElement?.let { el ->
//                    @Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")
//                    el.scroll(
//                        obj {
//                            left = el.scrollLeft - 150.0
//                            behavior = "smooth"
//                        } as ScrollToOptions,
//                    )
//                }
//            }
//        }
//
//        // CONTENT
//        content.invoke(this)
//
//        // Right > Chevron Button ("move to scroll-end")
//        twMediumIconButton(icon = FormationUIIcons.ChevronRight) {
//            className("absolute right-1 text-white bg-formationBlack/20 hover:bg-formationBlack/50 rounded-full p-1")
//            className(overflowRight.data.map { isOverflown -> if (isOverflown) "block" else "hidden" })
//            clicks handledBy {
//                domNode.parentElement?.let { el ->
//                    @Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")
//                    el.scroll(
//                        obj {
//                            left = el.scrollLeft + 150.0
//                            behavior = "smooth"
//                        } as ScrollToOptions,
//                    )
//                }
//            }
//        }
//    }
//}

